import React, { Suspense, useEffect, useState } from 'react';
import { compose } from 'lodash/fp';
import { Navigate, Outlet, Route, useLocation } from 'react-router-dom';
import { CardSpinner } from '../../../domain/card/component/CardSpinner';
import {
    AssistantContainerBaseConfig,
    AssistantControllerBaseConfig,
    AssistantLoaderBaseConfig,
    AssistantViewBaseConfig,
} from './base';
import {
    createThreadDetailContainer,
    createThreadDetailController,
    createThreadDetailLoader,
    createThreadDetailView,
    createThreadNewContainer,
    createThreadNewController,
    createConversationNewLoader,
    createThreadNewView,
    createThreadDetailContextProvider,
    createAssistantRootContainer,
    serializeNewThreadOptions,
    createThreadDetailService,
    createThreadComposerContainer,
    createThreadComposerController,
    ThreadDetailRouteProps,
    createThreadStateProvider,
    createAssistantRootLoader,
    createAssistantRootController,
    createLinkContainer,
    createLinkLoader,
    createLinkController,
} from './page';
import {
    createAssistantLayoutContainer,
    createAssistantLayoutController,
    createAssistantLayoutLoader,
    createAssistantMobileLayoutView,
    createAssistantDesktopLayoutView,
    createAssistantLayoutStateProvider,
} from './page';
import { createAssistantErrorView, createAssistantErrorContainer } from './page';
import { createAssistantMiddleware } from './middleware';
import {
    createButtonElementContainer,
    createButtonElementController,
    createVisualizationBlockContainer,
    createVisualizationBlockController,
} from './view';
import { AssistantNavigation } from './navigate';
import { InterviewRouterConfig, InterviewRouteInit } from './interviewConfig';
import { InterviewRouter, InterviewRouterEnhancer } from './interviewInterface';
import { assert } from '@varos/util-typescript';
import { Center, DarkMode, HStack, Text, VStack } from '@chakra-ui/react';
import {
    createAssistantContext,
    createContextController,
    createContextLoader,
    createContextContainer,
} from './page/context';
import {
    createThreadOverviewContainer,
    createThreadOverviewController,
    createThreadOverviewLoader,
    createThreadOverviewView,
} from './page/thread/overview';

export function configureInterviewRouter(
    config: InterviewRouteInit,
    enhancer?: InterviewRouterEnhancer
): InterviewRouter {
    const middleware = createAssistantMiddleware();
    const enhancers = enhancer ? [enhancer, middleware] : [middleware];
    return create(
        {
            ...config,
            provider: {
                createThreadDetailController,
                createThreadNewController,
                createButtonElementController,
                createVisualizationBlockController,
            },
        },
        compose(...enhancers)
    );
}

function create(
    config: InterviewRouterConfig,
    enhancer?: InterviewRouterEnhancer
): InterviewRouter {
    if (enhancer) {
        return enhancer(create)(config);
    }

    const navigation: AssistantNavigation = {
        home: {
            path: config.mount,
        },
        thread: {
            create(props) {
                const payload = serializeNewThreadOptions({
                    return_path: location.pathname,
                    thread: props.thread,
                });
                const stringPayload = Object.entries(payload).reduce(
                    (acc, [key, value]) => {
                        if (value === undefined || value === null) {
                            return acc;
                        }
                        return { ...acc, [key]: String(value) };
                    },
                    {} as Record<string, string>
                );
                const params = new URLSearchParams(stringPayload);
                return {
                    to: `/${config.mount}/new?${params.toString()}`.replaceAll(
                        /\/\//g,
                        '/'
                    ),
                };
            },
            detail(props) {
                const base = [
                    `/${config.mount}/${props.thread.id}`.replaceAll(/\/\//g, '/'),
                ];
                let payload: ThreadDetailRouteProps = {
                    token: null,
                    mode: null,
                    return_path: props.returnPath,
                };
                if (props.mode === 'voice') {
                    payload = { ...payload, mode: props.mode, token: props.token };
                }
                const stringPayload = Object.entries(payload).reduce(
                    (acc, [key, value]) => {
                        if (value === undefined || value === null) {
                            return acc;
                        }
                        return { ...acc, [key]: String(value) };
                    },
                    {} as Record<string, string>
                );
                const params = new URLSearchParams(stringPayload);
                base.push(params.toString());
                return {
                    to: base.join('?'),
                };
            },
        },
    };

    const contexts = {
        root: createAssistantContext(),
    };

    const containerConfig: AssistantContainerBaseConfig = {
        adapter: config.adapter,
        UI: config.UI,
        repository: config.repository,
        kernel: config.kernel,
        navigation,
        context: {
            thread: createThreadDetailContextProvider(),
            root: contexts.root,
            // root: {
            //     useContext() {
            //         const account = config.hook.useAccount?.();
            //         if (!account?.scope.auth.user) {
            //             return {
            //                 principal: null,
            //             };
            //         }
            //         assert(
            //             account?.scope.auth.scheme.kind === 'legacy',
            //             'expected legacy auth scheme'
            //         );
            //         return {
            //             principal: {
            //                 kind: 'user',
            //                 id: account.scope.auth.user.id,
            //                 account: account.data.account.id,
            //             },
            //             token: account.scope.auth.scheme.store.authToken!,
            //             organization: null,
            //         };
            //     },
            // },
        },
    };

    const loaderConfig: AssistantLoaderBaseConfig = {
        repository: config.repository,
        kernel: config.kernel,
        adapter: config.adapter,
    };

    const controllerConfig: AssistantControllerBaseConfig = {
        navigation: navigation,
        kernel: config.kernel,
        infra: config.infra,
        controller: config.controller,
    };

    const viewConfig: AssistantViewBaseConfig = {
        UI: config.UI,
        Layout: createAssistantLayoutContainer(
            containerConfig,
            createAssistantLayoutStateProvider(containerConfig),
            createAssistantLayoutLoader(),
            createAssistantLayoutController(controllerConfig),
            {
                Desktop: createAssistantDesktopLayoutView({
                    UI: config.UI,
                }),
                Mobile: createAssistantMobileLayoutView({
                    UI: config.UI,
                }),
            }
        ),
    };

    const Context = createContextContainer(
        containerConfig,
        contexts.root,
        createContextLoader(loaderConfig),
        createContextController(controllerConfig)
    );

    const Common = {
        Error: createAssistantErrorContainer(
            containerConfig,
            createAssistantErrorView(viewConfig)
        ),
    };

    const Root = createAssistantRootContainer(
        containerConfig,
        createAssistantRootLoader(loaderConfig),
        createAssistantRootController(controllerConfig),
        {
            Error: createAssistantErrorContainer(
                containerConfig,
                createAssistantErrorView(viewConfig)
            ),
        }
    );

    const Link = {
        Detail: createLinkContainer(
            containerConfig,
            createLinkLoader(loaderConfig),
            createLinkController(controllerConfig)
        ),
    };

    const Thread = {
        Overview: createThreadOverviewContainer(
            containerConfig,
            createThreadOverviewLoader(loaderConfig),
            createThreadOverviewController(controllerConfig),
            createThreadOverviewView(viewConfig)
        ),
        Detail: createThreadDetailContainer(
            containerConfig,
            createThreadStateProvider(containerConfig),
            createThreadDetailService(),
            createThreadDetailLoader(loaderConfig),
            config.provider.createThreadDetailController(controllerConfig),
            createThreadDetailView(
                {
                    ...viewConfig,
                    Container: {
                        Composer: createThreadComposerContainer(
                            containerConfig,
                            createThreadStateProvider(containerConfig),
                            createThreadComposerController(controllerConfig)
                        ),
                    },
                },
                {
                    Button: createButtonElementContainer(
                        containerConfig,
                        config.provider.createButtonElementController()
                    ),
                    Visualization: createVisualizationBlockContainer(
                        containerConfig,
                        config.provider.createVisualizationBlockController()
                    ),
                }
            )
        ),
        New: createThreadNewContainer(
            containerConfig,
            createConversationNewLoader(loaderConfig),
            config.provider.createThreadNewController(controllerConfig),
            createThreadNewView(viewConfig)
        ),
    };

    // A tiny component that only renders its fallback after `delay` ms
    const DelayedFallback: React.FC<{
        delay: number;
        children: React.ReactNode;
    }> = ({ delay, children }) => {
        const [ready, setReady] = useState(false);

        useEffect(() => {
            const handle = window.setTimeout(() => setReady(true), delay);
            return () => window.clearTimeout(handle);
        }, [delay]);

        // render nothing until the timeout fires
        return ready ? <>{children}</> : <CardSpinner />;
    };

    const {
        UI: {
            Application: { createErrorBoundary },
        },
    } = config;

    const ErrorBoundary = createErrorBoundary({
        errorClass: Error,
    });

    const ErrorBoundaryWrapper: React.FC<React.PropsWithChildren> = (props) => {
        const location = useLocation();
        return (
            <ErrorBoundary
                key={location.pathname}
                fallback={(info) => <Common.Error info={info} />}
            >
                {props.children}
            </ErrorBoundary>
        );
    };

    return {
        navigation: navigation,
        element: (
            <Route
                element={
                    <ErrorBoundaryWrapper>
                        <Context>
                            <Suspense
                                fallback={
                                    <Center w="full" h="full">
                                        <CardSpinner />
                                    </Center>
                                }
                            >
                                <Root>
                                    <Outlet />
                                </Root>
                            </Suspense>
                        </Context>
                    </ErrorBoundaryWrapper>
                }
            >
                <Route index={true} element={<Thread.Overview />} />
                <Route path="link" element={<Link.Detail />} />
                <Route path="new" element={<Thread.New />} />
                <Route
                    path=":threadId"
                    element={
                        <Suspense
                            fallback={
                                <DelayedFallback delay={6000}>
                                    <Center w="full" h="full">
                                        <VStack align="center" spacing={4}>
                                            <CardSpinner />
                                            <VStack spacing={1}>
                                                <Text
                                                    fontWeight="semibold"
                                                    color="whiteAlpha.700"
                                                >
                                                    It's taking longer than usual
                                                </Text>
                                                <Text color="whiteAlpha.500">
                                                    If still waiting after 10 seconds try
                                                    refreshing your browser
                                                </Text>
                                            </VStack>
                                        </VStack>
                                    </Center>
                                </DelayedFallback>
                            }
                        >
                            <Thread.Detail />
                        </Suspense>
                    }
                />
            </Route>
        ),
    };
}
